import React, {useState, useEffect} from 'react';
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { isRegister, userToken, logout } from "../../features/user";
import axios from "axios";
import { API_OK, API_URL, DEFAULT_ERROR_TEXT, API_EXCEPTION } from '../../Constants';
import {Trans,  useTranslation } from 'react-i18next';
import {checkApiSessionErrorMessage, getLang} from "../../Helper"
import { useRetryCountdown } from "../../hooks/useRetryCountdown";
import RetryNotice from "../widgets/RetryNotice";

import Frame from '../widgets/Frame';
import VoiceCallNotice from "../widgets/VoiceCallNotice";
import { useSubmitGuard } from "../../hooks/useSubmitGuard";
import { useAutoFocus } from "../../hooks/useAutoFocus";
import { useWebOtp } from "../../hooks/useWebOtp";
import { useCaptcha } from "../../captcha";
import { useEnterSubmit } from '../../hooks/useEnterSubmit';

export function VerifyPhone() {
    const [state, setState] = useState({ code: "", errors: {}, message: null, phone:"",  communicationPref:""});
    

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    // One check per click: each submission spends a verification attempt.
    const submit = useSubmitGuard();
    /**
     * הקאפצ'ה. פעילה רק כשההגדרה הגיעה מהשרת, ומה שנשלח נבדק שם בלי קשר למסך.
     */
    const captcha = useCaptcha('verifyCode');
    const isUserRegister = useSelector(isRegister);
    const retry = useRetryCountdown();
    /* המסך כולו הקלדת קוד, והקוד יצא לפני שהוא נטען - ולכן הסמן בשדה מהטעינה.
       "שליחה חוזרת" כאן מנווטת למסך בחירת השיטה, ואין שדה שנשאר להחזיר לו סמן. */
    const [codeRef] = useAutoFocus();

    /**
     * מילוי אוטומטי של הקוד, כשהוא יצא במסרון.
     *
     * `communicationPref` מגיע מהשרת ומתאר את מה שנשלח **בפועל**, ולכן ההאזנה נפתחת
     * רק במסלול המסרון. השדה אינו נשלט (אין עליו `value`), ולכן הוא מתמלא גם ב-DOM.
     */
    useWebOtp(
        state.communicationPref === 'SMS',
        (code) => {
            if (codeRef.current) codeRef.current.value = code;
            setState((prev) => ({ ...prev, code, errors: {} }));
        },
        () => handleSubmit()
    );

    useEffect(()=>{
        if(!isUserRegister){
           navigate("/register");
        } else {
            getRegistrationData();
        }
        // eslint-disable-next-line
    },[]);


    const getRegistrationData = async (e) => {
        let errorMessage = DEFAULT_ERROR_TEXT;
        
        let config = isUserRegister?{'headers': {"token": userToken(), "lang": getLang()}}:{'headers': {"lang": getLang()}};
        try {
            const reply = await axios.get(API_URL + 'getRegistrationData', config);
            if(reply.hasOwnProperty('data') && reply.data.hasOwnProperty('data') && reply.data.hasOwnProperty('responseStatus') && reply.data.responseStatus === API_OK) {
                setState({
                    ...state,
                    phone: reply.data.data.phone,
                    communicationPref: reply.data.data.communicationPref
                });
            } else {
                if((reply.data.responseStatus === API_EXCEPTION) && (reply.data.hasOwnProperty("message"))){
                    errorMessage = reply.data["message"];
                }
                if(!checkApiSessionErrorMessage(errorMessage, dispatch, logout, navigate)) {
                    setState({
                        ...state,
                        errors: {server_error: errorMessage}
                    });
                }
            }
        } catch (e) {
            setState({
                ...state,
                errors: {server_error: errorMessage}
            });
        }
    }


    const handleSubmit = async (e) => {
        // אופציונלי, כי המילוי האוטומטי מגיש בלי אירוע. ראה `useWebOtp`.
        e?.preventDefault();
        if (retry.isWaiting) {
            return;
        }
        // validateForm() returns true when a field is invalid (it also
        // sets the field errors it found).
        if (validateForm()) {
            return;
        }

        let errorMessage = DEFAULT_ERROR_TEXT;

        let config = {'headers': {"token": userToken(), "lang": getLang()}};

        const data = {code: state.code.trim()};

        // הטוקן מצורף כאן, כדי שהפעולה שהוא נוצר עבורה תיקבע במקום אחד.
        const payload = await captcha.attach(data);
        // אין טוקן - אין מה לשלוח. ההסבר כבר מצויר על המסך.
        if (!payload) {
            return;
        }

        try {
            const reply = await submit.run(() => axios.post(API_URL + 'checkPhoneVerificationCode', payload, config));
            // A blocked second click returns nothing; the first one is still running.
            if (!reply) {
                return;
            }
            if(reply.hasOwnProperty('data') && reply.data.hasOwnProperty('responseStatus') && reply.data.responseStatus === API_OK){
                /**
                 * **חשבון שכבר קיים - נאמר כאן, ולא במסך הבא.**
                 *
                 * השרת בודק את זה מיד אחרי שהקוד אושר, כלומר ברגע הראשון שבו מותר
                 * לומר את זה: שני הערוצים כבר הוכחו. עד כאן ההרשמה המשיכה עוד מסך
                 * שלם, והנרשם קיבל `user_already_exists` מול טופס שאין בו מה לתקן.
                 */
                if (reply.data.accountExists) {
                    navigate("/account-exists");
                    return;
                }
                navigate("/create-credentials");
                return;
            }

            if((reply.data.responseStatus === API_EXCEPTION) && (reply.data.hasOwnProperty("message"))){
                errorMessage = reply.data["message"];
            }
            if(checkApiSessionErrorMessage(errorMessage, dispatch, logout, navigate)) {
                return;
            }

            let errors = {server_error: errorMessage};

            // Server reports the remaining lockout as fractional minutes;
            // turn it into a live countdown that gates the next button below.
            if((reply.data.responseStatus === API_EXCEPTION) && (reply.data.hasOwnProperty("tryAgainInMinutes"))){
                retry.startFromMinutes(reply.data["tryAgainInMinutes"]);
            }

            setState({
                ...state,
                errors: errors,
                message: null
            });
        } catch (e) {
            setState({
                ...state,
                errors: {server_error: errorMessage},
                message: null
            });
        }

    };

    const handleResendCode = async (e) => {
        e.preventDefault();
        navigate("/verification-phone-code-type");
    };

    const handleChange = (e) => {
        setState({
            ...state,
            [e.target.id]: e.target.value,
            errors: {},
            message: null
        });
    };

    const validateForm = () => {
        const errors = {};
        if (!state.code.trim() /*|| state.firstName.trim().length < 4*/) {
            errors['code'] = 'Verification Code is required';
        }

        setState({
            ...state,
            errors: errors,
            message: null
        });

        return Object.keys(errors).length > 0;
    };

    /**
     * Enter ממשיך לשלב הבא. ראה `useEnterSubmit` - הפעולה הראשית של המסך אינה
     * כפתור שליחה אלא `div`, ולכן הדפדפן אינו שולח כאן דבר מעצמו.
     */
    useEnterSubmit((e) => handleSubmit(e), !submit.busy);

    return (
        <Frame>
            <div className='main main-verify-phone'>
                <div className='main-top'>
                    <h1>{t("Verify your phone number")}</h1>
                </div>
                <div className='main-body'>
                    <div className='right-main'>
                        <div className='right-main-inner'>
                            {state.communicationPref === 'SMS' && <h2><Trans i18nKey="Great! We just sent you a verification code via SMS to the number <span>{{phone}}</span>. Please insert it here."
                                values={{ phone: state.phone }}
                                components={{
                                    span: <span className="ltr-span" />
                                }}
                            /></h2>}
                            {/*
                              * ההסבר על השיחה - הודעת אזהרה, ולא משפט ארוך בין שאר השורות.
                              *
                              * `communicationPref` מגיע מהשרת ומתאר את מה שנשלח בפועל, ולכן
                              * ההודעה מופיעה רק כשהשיחה יצאה. במסלול המסרון היא אינה נטענת.
                              */}
                            {state.communicationPref === 'VOICE' && <VoiceCallNotice phone={state.phone} />}
                            <div className='mt30 input blue-border'><input ref={codeRef} className={state.errors.code?"error":""}  type="text" id="code" inputMode="numeric" autoComplete="one-time-code" placeholder={state.communicationPref === 'VOICE' ? t("Last 4 digits") : t("Verification Code")} onChange={(e) => handleChange(e)} /></div>
                            {captcha.node}
                            <div {...submit.buttonProps({base: 'button button-space-between button-teal', blocked: retry.isWaiting})} onClick={(e) => handleSubmit(e)}>  <div className='mr20'>{submit.busy ? t("Checking the code...") : t("next")}</div> {submit.busy ? <span className='button-spinner ml20' /> : <img src='/images/less-than-white.png' className='ml20 button-icon less-than-icon' alt=''/>} </div>
                            <h2 className='mt40 teal pointer' onClick={(e) => handleResendCode(e)}><Trans i18nKey="I didn't receive the code, <strong>please resend it</strong>" /></h2>
                            {state.message?<h2 className='teal'><Trans i18nKey={state.message} /></h2>:""}
                            {Object.entries(state.errors).map(([key, msg]) => (
                                <h2 key={key} className='mt20 error'><Trans i18nKey={msg} /></h2>
                            ))}
                            <RetryNotice retry={retry} />
                        </div>
                    </div>    
                    <div className='left-main'>
                        <img src='/images/person-computer.png' className='content-logo' alt=''/>
                    </div>
                </div>
            </div>
        </Frame>
    );
}